import { observer } from "mobx-react";
import React, { useState } from "react";

import { Button } from "@components/buttons";
import classNames from "classnames";

import RecentGames from "./RecentGames";

export interface IGraphProps {
	title?: string;
	home?: string;
	away?: string;
	secondaryInfo?: boolean;
	info?: string;
	removeValues?: boolean;
	graphName?: string;
	className?: string;
	data?: {
		home?: string;
		away?: string;
		homeValue?: number;
		awayValue?: number;
	};
	matchNames?: {
		home?: string;
		away?: string;
	};
	max?: number;
	maxHome?: number;
	maxAway?: number;
	isPercentage?: boolean;
	isTotalGraph?: boolean;
	eventCount?: {
		home?: number;
		away?: number;
	};
	noBorder?: boolean;
	highlightTeam?: { homeTeamHighlight: boolean; awayTeamHighlight: boolean };
	isNotUnique?: boolean;
}

export default observer(function Graph(props: IGraphProps) {
	if (
		(props.data?.homeValue == 0 && props.data?.awayValue == 0) ||
		(props.data?.homeValue == null && props.data?.awayValue == null)
	) {
		console.error(
			`Home or away team values are null; expected values for ${props?.graphName}`
		);
		return null;
	}

	function calculateProgressValue(
		teamValue: number | undefined,
		isTotalGraph: boolean | undefined
	) {
		if (teamValue == 0) {
			return 0;
		} else if (isTotalGraph) {
			return 100;
		} else {
			return teamValue || 0;
		}
	}

	if (props.noBorder) {
		return (
			<>
				{props.title && (
					<div className="card__header card--stats__header">
						<div className="card__title card__title--base card--stats__heading">
							{props.title}
						</div>
					</div>
				)}

				<div className="card--stats__section">
					{props.eventCount && (
						<RecentGames
							homeTeamEventCount={props.eventCount.home}
							awayTeamEventCount={props.eventCount.away}
						/>
					)}

					<>
						{props.secondaryInfo || props.info ? (
							<InfoSection {...props} />
						) : (
							<div className="card--stats__row">
								{props.home && (
									<div className="card--stats__col card--stats__col--left">
										<div className="card--stats__label">
											{props.home}
										</div>
									</div>
								)}

								{props.graphName && (
									<div className="card--stats__col card--stats__col--middle">
										<div className="card--stats__label u-type--xxsml">
											{props.graphName}
										</div>
									</div>
								)}

								{props.away && (
									<div className="card--stats__col card--stats__col--right">
										<div className="card--stats__label">
											{props.away}
										</div>
									</div>
								)}
							</div>
						)}

						<div className="card--stats__row">
							<div className="card--stats__col card--stats__col--left">
								<div className="chart chart--left is-home">
									{!props.removeValues && (
										<label
											htmlFor="chartHome"
											className="chart__value"
										>
											{props.data?.homeValue}
											{props.isPercentage && (
												<span>%</span>
											)}
										</label>
									)}
									<progress
										id="chartHome"
										className={classNames(
											"chart__progress",
											{
												"is-transparent":
													props.highlightTeam
														?.awayTeamHighlight,
											}
										)}
										value={calculateProgressValue(
											props.data?.homeValue,
											props.isTotalGraph
										)}
										max={
											props?.max || props?.maxHome || 100
										}
									/>
								</div>
							</div>

							<div className="card--stats__col card--stats__col--right">
								<div className="chart chart--right is-away">
									{!props.removeValues && (
										<label
											htmlFor="chartAway"
											className="chart__value"
										>
											{props.data?.awayValue}
											{props.isPercentage && (
												<span>%</span>
											)}
										</label>
									)}
									<progress
										id="chartAway"
										className={classNames(
											"chart__progress",
											{
												"is-transparent":
													props.highlightTeam
														?.homeTeamHighlight,
											}
										)}
										value={calculateProgressValue(
											props.data?.awayValue,
											props.isTotalGraph
										)}
										max={
											props?.max || props?.maxAway || 100
										}
									/>
								</div>
							</div>
						</div>
					</>
				</div>
			</>
		);
	}

	return (
		<div className="card card--raised card--raised--framed card--stats">
			{props.title && (
				<div className="card__header card--stats__header">
					<div className="card__title card__title--base card--stats__heading">
						{props.title}
					</div>
				</div>
			)}

			<div className="card--stats__body">
				{props.eventCount && (
					<RecentGames
						homeTeamEventCount={props.eventCount.home}
						awayTeamEventCount={props.eventCount.away}
					/>
				)}

				<div className="card--stats__section">
					{props.secondaryInfo || props.info ? (
						<InfoSection {...props} />
					) : (
						<div className="card--stats__row">
							{props.home && (
								<div className="card--stats__col card--stats__col--left">
									<div className="card--stats__label">
										{props.home}
									</div>
								</div>
							)}

							{props.graphName && (
								<div className="card--stats__col card--stats__col--middle">
									<div className="card--stats__label u-color--text--secondary">
										{props.graphName}
									</div>
								</div>
							)}

							{props.away && (
								<div className="card--stats__col card--stats__col--right">
									<div className="card--stats__label">
										{props.away}
									</div>
								</div>
							)}
						</div>
					)}

					<div className="card--stats__row">
						<div className="card--stats__col card--stats__col--left">
							<div className="chart chart--left is-home">
								<label
									htmlFor="chartHome"
									className="chart__value"
								>
									{!props.removeValues && (
										<label
											htmlFor="chartAway"
											className="chart__value"
										>
											{props.data?.homeValue}
											{props.isPercentage && (
												<span>%</span>
											)}
										</label>
									)}
								</label>
								<progress
									id="chartHome"
									className={classNames("chart__progress", {
										"is-transparent":
											props.highlightTeam
												?.awayTeamHighlight,
									})}
									value={calculateProgressValue(
										props.data?.homeValue,
										props.isTotalGraph
									)}
									max={props?.max || props?.maxHome || 100}
								/>
							</div>
						</div>

						<div className="card--stats__col card--stats__col--right">
							<div className="chart chart--right is-away">
								{!props.removeValues && (
									<label
										htmlFor="chartAway"
										className="chart__value"
									>
										{props.data?.awayValue}
										{props.isPercentage && <span>%</span>}
									</label>
								)}
								<progress
									id="chartAway"
									className={classNames("chart__progress", {
										"is-transparent":
											props.highlightTeam
												?.homeTeamHighlight,
									})}
									value={calculateProgressValue(
										props.data?.awayValue,
										props.isTotalGraph
									)}
									max={props?.max || props?.maxAway || 100}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
});

const InfoSection = (props: IGraphProps) => {
	const [showInfoSection, setShowInfoSection] = useState(false);

	return (
		<>
			<div className="card--stats__row">
				<div className="card--stats__col card--stats__col--left">
					<div className="card--stats__value u-color--stats--home">
						{props.data?.home || (
							<>
								{props.data?.home || props.data?.homeValue}
								{props.isPercentage && <>%</>}
							</>
						)}
					</div>
				</div>

				<div className="card--stats__col card--stats__col--middle card--stats__col--grow">
					{props.graphName && (
						<div className="card--stats__label">
							{props.graphName}
						</div>
					)}

					{!props.secondaryInfo && (
						<Button
							className="btn--square btn--square--xsml btn--ghost card--stats__info"
							iconOnly={true}
							iconClassName={classNames("u-icon--sml", {
								"u-icon--info-circle": !showInfoSection,
								"u-icon--info-circle--bold": showInfoSection,
							})}
							onClick={() => setShowInfoSection(!showInfoSection)}
						/>
					)}
				</div>

				<div className="card--stats__col card--stats__col--right">
					<div className="card--stats__value u-color--stats--away">
						{props.data?.away || (
							<>
								{props.data?.away || props.data?.awayValue}
								{props.isPercentage && <>%</>}
							</>
						)}
					</div>
				</div>
			</div>
			{showInfoSection && (
				<div className="message message--base message--info u-type--sml u-mar--bottom--xsml">
					{props.info}
				</div>
			)}
		</>
	);
};
